/* eslint react-hooks/exhaustive-deps: "warn" */

import { useEffect, useRef, useState } from 'react';

import cn from 'classnames';

import { useIntersection } from 'hooks/useIntersection';

import styles from './OwnershipGraphCard.module.scss';

import { OWNERSHIP_GRAPH_SVG } from './OwnershipGraphCard.svg';
import type { GraphTab, GraphView, OwnershipGraphCardProps } from './OwnershipGraphCard.types';

const TABS: { key: GraphTab; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'sub', label: 'Subsidiaries' },
  { key: 'mgmt', label: 'Management' },
  { key: 'sh', label: 'Shareholders' },
  { key: 'screen', label: 'Screening' },
];

const ORDER: GraphTab[] = ['all', 'sub', 'mgmt', 'sh', 'screen'];

// [companies, active officers, shareholders] per tab — matches the mockup counters.
const TAB_STATS: Record<GraphTab, [number, number, number]> = {
  all: [6, 1, 0],
  sub: [6, 0, 0],
  mgmt: [6, 1, 0],
  sh: [1, 0, 27],
  screen: [6, 1, 27],
};

type RowKind = 'core' | 'of' | 'co' | 'sh';

interface ListRow {
  entity: string;
  rel: string;
  juris: string;
  source: string;
  kind: RowKind;
}

const LIST_ROWS: ListRow[] = [
  {
    entity: 'KESSLER GROUP HOLDINGS LTD',
    rel: 'PSC · 52.4% — Martin Kessler',
    juris: '🇬🇧 United Kingdom',
    source: 'Companies House',
    kind: 'core',
  },
  {
    entity: 'Martin Kessler',
    rel: 'Director · Person with significant control',
    juris: '—',
    source: 'Companies House · PSC',
    kind: 'of',
  },
  {
    entity: 'Kessler Travel Ltd',
    rel: 'Director — Martin Kessler',
    juris: '🇬🇧 United Kingdom',
    source: 'Companies House',
    kind: 'co',
  },
  {
    entity: 'Kessler Securities Europe Ltd',
    rel: 'Director — Martin Kessler',
    juris: '🇮🇪 Ireland',
    source: 'CRO Ireland',
    kind: 'co',
  },
  {
    entity: 'Kessler GmbH Niederlassung',
    rel: 'Representative — Martin Kessler',
    juris: '🇩🇪 Germany',
    source: 'Handelsregister',
    kind: 'co',
  },
  {
    entity: 'Kessler Trading Ltd',
    rel: 'Director — Martin Kessler',
    juris: '🇬🇧 United Kingdom',
    source: 'Companies House',
    kind: 'co',
  },
  {
    entity: 'Kessler Ltd',
    rel: 'Director — Martin Kessler',
    juris: '🇬🇧 United Kingdom',
    source: 'Companies House',
    kind: 'co',
  },
  { entity: 'O. Urbas', rel: 'Shareholder', juris: '🇬🇧 United Kingdom', source: 'Companies House', kind: 'sh' },
  { entity: 'R. Advani', rel: 'Shareholder', juris: '🇬🇧 United Kingdom', source: 'Companies House', kind: 'sh' },
  { entity: 'A. Mahmud', rel: 'Shareholder', juris: '🇬🇧 United Kingdom', source: 'Companies House', kind: 'sh' },
  { entity: 'Y. Chai', rel: 'Shareholder', juris: '🇬🇧 United Kingdom', source: 'Companies House', kind: 'sh' },
  { entity: 'A. Wong', rel: 'Shareholder', juris: '🇬🇧 United Kingdom', source: 'Companies House', kind: 'sh' },
  { entity: '+ 21 more shareholders', rel: 'Shareholder', juris: '', source: 'Companies House', kind: 'sh' },
];

const ROW_TABS: Record<RowKind, GraphTab[]> = {
  core: ['all', 'sub', 'mgmt', 'sh'],
  of: ['all', 'mgmt'],
  co: ['all', 'sub', 'mgmt'],
  sh: ['all', 'sh'],
};

interface ScreenRow {
  icon: string;
  person?: boolean;
  name: string;
  meta: string;
  flag?: boolean;
  badges: { label: string; tone: 'ok' | 'rev' }[];
}

const SCREEN_ROWS: ScreenRow[] = [
  {
    icon: '👤',
    person: true,
    name: 'Martin Kessler',
    meta: 'Person · Director & PSC',
    badges: [
      { label: 'Sanctions · Clear', tone: 'ok' },
      { label: 'PEP · Clear', tone: 'ok' },
    ],
  },
  {
    icon: '🇬🇧',
    name: 'Kessler Group Holdings Ltd',
    meta: 'Company · United Kingdom',
    badges: [
      { label: 'Sanctions · Clear', tone: 'ok' },
      { label: 'PEP · Clear', tone: 'ok' },
    ],
  },
  {
    icon: '🇬🇧',
    name: 'Kessler Trading Ltd',
    meta: 'Company · United Kingdom',
    badges: [
      { label: 'Sanctions · Clear', tone: 'ok' },
      { label: 'PEP · Clear', tone: 'ok' },
    ],
  },
  {
    icon: '👤',
    person: true,
    name: 'A. Mahmud',
    meta: 'Shareholder · 3.1%',
    flag: true,
    badges: [
      { label: 'Sanctions · Clear', tone: 'ok' },
      { label: 'PEP · Review', tone: 'rev' },
    ],
  },
  {
    icon: '🇩🇪',
    name: 'Kessler GmbH Niederlassung',
    meta: 'Company · Germany',
    badges: [
      { label: 'Sanctions · Clear', tone: 'ok' },
      { label: 'PEP · Clear', tone: 'ok' },
    ],
  },
  {
    icon: '＋',
    name: '29 more companies & individuals',
    meta: 'Screened in the same pass',
    badges: [{ label: 'All clear', tone: 'ok' }],
  },
];

const SOURCE_PILLS = ['Companies House · PSC register', 'CRO Ireland', 'Handelsregister Germany'];

export const OwnershipGraphCard = ({ className }: OwnershipGraphCardProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useIntersection(ref, { continuous: true, threshold: 0.3 });

  const [tab, setTab] = useState<GraphTab>('all');
  const [view, setView] = useState<GraphView>('graph');
  const [interacted, setInteracted] = useState(false);
  const [reduced, setReduced] = useState(false);
  const [stats, setStats] = useState<[number, number, number]>([0, 0, 0]);

  const statsRef = useRef<[number, number, number]>([0, 0, 0]);
  const rafRef = useRef<number>(undefined);
  const stopped = useRef(false);

  useEffect(() => {
    setReduced(window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }, []);

  // Tween the three counters towards the active tab's target.
  useEffect(() => {
    const target = TAB_STATS[tab];

    if (reduced) {
      statsRef.current = target;
      setStats(target);

      return;
    }

    const from = statsRef.current;
    const t0 = performance.now();
    const ms = 420;

    if (rafRef.current) {
      window.cancelAnimationFrame(rafRef.current);
    }

    const tick = () => {
      const k = Math.min(1, (performance.now() - t0) / ms);
      const e = 1 - Math.pow(1 - k, 3);
      const next: [number, number, number] = [
        Math.round(from[0] + (target[0] - from[0]) * e),
        Math.round(from[1] + (target[1] - from[1]) * e),
        Math.round(from[2] + (target[2] - from[2]) * e),
      ];
      statsRef.current = next;
      setStats(next);
      if (k < 1) {
        rafRef.current = window.requestAnimationFrame(tick);
      }
    };

    rafRef.current = window.requestAnimationFrame(tick);

    return () => {
      if (rafRef.current) {
        window.cancelAnimationFrame(rafRef.current);
      }
    };
  }, [tab, reduced]);

  // Auto-tour: cycle the tabs until the user takes over or the card leaves the viewport.
  useEffect(() => {
    if (reduced || stopped.current || !inView) {
      return;
    }

    const timer = window.setInterval(() => {
      setTab((prev) => ORDER[(ORDER.indexOf(prev) + 1) % ORDER.length]);
    }, 4200);

    return () => window.clearInterval(timer);
  }, [reduced, inView]);

  const takeOver = () => {
    stopped.current = true;
    setInteracted(true);
  };

  const handleTab = (next: GraphTab) => {
    takeOver();
    setTab(next);
  };

  const handleView = (next: GraphView) => {
    takeOver();
    setView(next);
  };

  const visibleRows = LIST_ROWS.filter((row) => ROW_TABS[row.kind].includes(tab));

  return (
    <div
      ref={ref}
      className={cn(styles.plat, (interacted || reduced) && styles.instant, className)}
      data-tab={tab}
      data-view={view}
    >
      <div className={styles.top}>
        <span className={styles.avatar} aria-hidden="true">
          MK
        </span>
        <div className={styles.id}>
          <h3>Martin Kessler</h3>
          <p>Total companies: 3 · Active positions: 12 · Countries: 4</p>
        </div>
      </div>

      <div className={styles.bar}>
        <div className={styles.tabset} role="tablist" aria-label="Filter relationships">
          {TABS.map((t) => (
            <button
              key={t.key}
              type="button"
              role="tab"
              aria-selected={t.key === tab}
              className={cn(t.key === tab && styles.on)}
              onClick={() => handleTab(t.key)}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className={styles.viewset} role="tablist" aria-label="View">
          <button
            type="button"
            role="tab"
            aria-selected={view === 'graph'}
            className={cn(view === 'graph' && styles.on)}
            onClick={() => handleView('graph')}
          >
            ⌥ Graph
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={view === 'list'}
            className={cn(view === 'list' && styles.on)}
            onClick={() => handleView('list')}
          >
            ☰ List
          </button>
        </div>
      </div>

      <div className={styles.canvas}>
        <span className={styles.legend} aria-hidden="true">
          LEGEND
        </span>

        <span className={styles.statsBox} aria-hidden="true">
          <span>
            <b className={styles.b}>{stats[0]}</b> Companies
          </span>
          <span>
            <b className={styles.g}>{stats[1]}</b> Active Officer
          </span>
          <span>
            <b className={styles.o}>{stats[2]}</b> Shareholders
          </span>
        </span>

        {/* Static data-viz — hardcoded markup, no user input. */}
        <div className={styles.svgHost} aria-hidden="true" dangerouslySetInnerHTML={{ __html: OWNERSHIP_GRAPH_SVG }} />

        <div className={styles.list}>
          <div className={styles.listTableScroll}>
            <table>
              <thead>
                <tr>
                  <th>Entity</th>
                  <th>Relationship</th>
                  <th>Jurisdiction</th>
                  <th>Source</th>
                </tr>
              </thead>
              <tbody>
                {visibleRows.map((row) => (
                  <tr key={row.entity}>
                    <td className={styles.ent}>{row.entity}</td>
                    <td>{row.rel}</td>
                    <td>{row.juris}</td>
                    <td>
                      <span className={styles.srcPill}>{row.source}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className={styles.scr}>
          <div className={styles.scrSum}>
            <span className={styles.scrChip}>
              <b>34</b> screened across the network
            </span>
            <span className={cn(styles.scrChip, styles.scrChipOk)}>
              <b>0</b> sanctions matches
            </span>
            <span className={cn(styles.scrChip, styles.scrChipRev)}>
              <b>1</b> PEP · review
            </span>
            <span className={styles.scrLists}>OFAC · EU · UN · UK HMT consolidated lists</span>
          </div>

          <div className={styles.scrRows}>
            {SCREEN_ROWS.map((row) => (
              <div key={row.name} className={cn(styles.scrRow, row.flag && styles.scrRowFlag)}>
                <span className={cn(styles.scrIc, row.person && styles.scrIcPerson)}>{row.icon}</span>
                <span className={styles.scrName}>
                  {row.name}
                  <small>{row.meta}</small>
                </span>
                {row.badges.map((badge) => (
                  <span
                    key={badge.label}
                    className={cn(styles.bdg, badge.tone === 'ok' ? styles.bdgOk : styles.bdgRev)}
                  >
                    {badge.label}
                  </span>
                ))}
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className={styles.src}>
        <span>Sources:</span>
        {SOURCE_PILLS.map((pill) => (
          <span key={pill} className={styles.srcPill}>
            {pill}
          </span>
        ))}
        <span className={styles.live}>
          <i />
          Resolved from 3 registries
        </span>
      </div>
    </div>
  );
};
